import { getTypeOrNull } from '@spotify/confidence-graphql';
import {
  TaskFragment,
  TaskState,
} from '@spotify/plugin-confidence-issues-react';

export const TASK_STATE_LABELS: Record<TaskState, string> = {
  [TaskState.Open]: 'Open',
  [TaskState.InProgress]: 'In progress',
  [TaskState.Completed]: 'Completed',
  [TaskState.Failed]: 'Failed',
  [TaskState.WaitingForInput]: 'Waiting for input',
};

const compareTasksByCreateTime = (
  firstTask: TaskFragment,
  secondTask: TaskFragment,
): number =>
  firstTask.createTime.localeCompare(secondTask.createTime) ||
  firstTask.name.localeCompare(secondTask.name);

const getTaskDependencyNames = (task: TaskFragment): string[] =>
  task.dependencies
    .map(dependency => getTypeOrNull(dependency)?.name)
    .filter((name): name is string => name !== undefined);

export const sortTasksByDependencyOrder = (
  tasks: TaskFragment[],
): TaskFragment[] => {
  const tasksByName = new Map(tasks.map(task => [task.name, task]));
  const remainingTaskNames = new Set(tasks.map(task => task.name));
  const dependenciesByTaskName = new Map(
    tasks.map(task => [
      task.name,
      getTaskDependencyNames(task).filter(dependencyName =>
        tasksByName.has(dependencyName),
      ),
    ]),
  );
  const orderedTasks: TaskFragment[] = [];

  while (remainingTaskNames.size > 0) {
    const readyTasks = Array.from(remainingTaskNames)
      .map(taskName => tasksByName.get(taskName))
      .filter((task): task is TaskFragment => task !== undefined)
      .filter(task =>
        dependenciesByTaskName
          .get(task.name)
          ?.every(dependencyName => !remainingTaskNames.has(dependencyName)),
      )
      .sort(compareTasksByCreateTime);

    if (readyTasks.length === 0) {
      const unresolvedTasks = Array.from(remainingTaskNames)
        .map(taskName => tasksByName.get(taskName))
        .filter((task): task is TaskFragment => task !== undefined)
        .sort(compareTasksByCreateTime);

      orderedTasks.push(...unresolvedTasks);
      break;
    }

    orderedTasks.push(...readyTasks);
    readyTasks.forEach(task => remainingTaskNames.delete(task.name));
  }

  return orderedTasks;
};
